<script setup lang="ts">
import { computed } from 'vue'

import { roleIcon, roleLabel } from '@/lib/format'
import { apexUrl, baseDomain } from '@/lib/host'
import { useSession } from '@/stores/session'
import type { Space } from '@/lib/types'

const emit = defineEmits<{ navigated: [] }>()

const { state, currentSpace, goToSpace } = useSession()

/** Owned spaces first, then the ones joined, both in the order the API gave. */
const ordered = computed<Space[]>(() => {
  const owned = state.spaces.filter((s) => s.role === 'owner')
  const rest = state.spaces.filter((s) => s.role !== 'owner')
  return [...owned, ...rest]
})

function choose(space: Space) {
  emit('navigated')
  // Cross-origin: a full page load, because each space is its own origin.
  goToSpace(space)
}
</script>

<template>
  <!-- daisyUI dropdown: open/close is CSS-driven off :focus-within, so both the
       trigger and the content take tabindex="0" and no JS state is needed. -->
  <div class="dropdown w-full">
    <div
      tabindex="0"
      role="button"
      class="btn w-full justify-between border-0 bg-base-100 font-normal"
    >
      <span class="flex min-w-0 flex-1 flex-col items-start">
        <span class="truncate text-sm font-medium">
          {{ currentSpace?.name || 'Select a space' }}
        </span>
        <span class="tnum truncate text-xs font-normal text-base-content/50">
          {{
            currentSpace
              ? `${currentSpace.url}.${baseDomain}`
              : `${state.spaces.length} available`
          }}
        </span>
      </span>
      <i class="fa-solid fa-chevron-down text-xs opacity-50" aria-hidden="true" />
    </div>

    <ul
      tabindex="0"
      class="dropdown-content menu z-50 mt-1 w-full rounded-box border border-base-content/10 bg-base-100 p-1 shadow-lg"
    >
      <li v-for="space in ordered" :key="space.id">
        <button
          :class="space.id === currentSpace?.id ? 'menu-active' : ''"
          @click="choose(space)"
        >
          <i
            :class="[
              'w-3 text-center text-xs',
              roleIcon(space.role ?? 'member'),
              space.role === 'owner' ? 'text-primary' : 'opacity-40',
            ]"
            :title="roleLabel(space.role ?? 'member')"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate">{{ space.name }}</span>
            <span class="tnum block truncate text-xs opacity-50">
              {{ space.url }}
            </span>
          </span>
          <i
            v-if="space.id === currentSpace?.id"
            class="fa-solid fa-check text-xs text-primary"
            aria-hidden="true"
          />
        </button>
      </li>

      <li v-if="ordered.length === 0" class="menu-disabled">
        <span>No spaces yet</span>
      </li>

      <li class="mt-1 border-t border-base-content/10 pt-1">
        <a :href="apexUrl('/create-space')" class="text-primary" @click="emit('navigated')">
          <i class="fa-solid fa-plus text-xs" aria-hidden="true" />
          Create Space
        </a>
      </li>
    </ul>
  </div>
</template>
